import React, { useState, useEffect } from 'react';
 
import { lang } from '../config/lang';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const DevisGenerator = ({ 
  formData, 
  selectedSubcategories, 
  subcategorySurfaces, 
  customDescription,
  descriptionMode,
  projectType = 'services' 
}) => {
  const [pricing, setPricing] = useState(null);
  const [unifiedStructure, setUnifiedStructure] = useState(null);

  useEffect(() => {
    if (formData?.selectedCategory) {
      processFormData();
    }
  }, [formData, selectedSubcategories, subcategorySurfaces, customDescription, descriptionMode]);

  const processFormData = async () => {
    let structuredData = null;

    // Mode texte libre désactivé (pas d'IA). Inviter à utiliser la sélection manuelle.
    if (descriptionMode === 'freeText') {
      setUnifiedStructure(null);
      setPricing(null);
      return;
    }

    // Sélection manuelle de catégories/sous-catégories
    if (descriptionMode === 'categories' && selectedSubcategories.length > 0) {
      structuredData = convertManualSelectionToStructure();
    }

    if (structuredData) {
      setUnifiedStructure(structuredData);

      // Générer les prix localement (fallback sans serveur ni IA)
      const pricingData = generatePricingEstimateLocal(
        structuredData.categories,
        structuredData.surfaces
      );
      setPricing(pricingData);
    } else {
      setUnifiedStructure(null);
      setPricing(null);
    }
  };

  // Estimation de prix locale (fallback) sans IA
  const generatePricingEstimateLocal = (categories, surfaces) => {
    const basePrices = {
      kitchen: { "Plan de travail": 200, "Électroménager": 150, "Placards": 180, "Évier": 120, "Robinetterie": 80 },
      bathroom: { "Douche": 250, "Baignoire": 300, "Lavabo": 150, "WC": 200, "Carrelage": 60, "Robinetterie": 100 },
      bedroom: { "Parquet": 80, "Peinture": 25, "Placards": 180, "Électricité": 50, "Fenêtres": 400 },
      living: { "Parquet": 80, "Carrelage": 60, "Peinture": 25, "Cheminée": 800, "Éclairage": 60 },
      flooring: { "Parquet": 80, "Carrelage": 60, "Lino/PVC": 40, "Moquette": 30, "Béton ciré": 100 },
      painting: { "Peinture murale": 25, "Papier peint": 35, "Enduits décoratifs": 45, "Placo": 30 },
      plumbing: { "Tuyauterie": 80, "Robinetterie": 100, "Sanitaires": 200, "Chauffage": 150 },
      electrical: { "Tableau électrique": 800, "Prises": 60, "Éclairage": 60, "Interrupteurs": 40 },
      facade: { "Ravalement": 50, "Isolation": 120, "Peinture": 30, "Bardage": 80 },
      roof: { "Couverture": 100, "Charpente": 150, "Isolation": 40, "Gouttières": 30 },
      garden: { "Terrasse": 80, "Jardin": 25, "Clôture": 60, "Portail": 400, "Éclairage": 50 },
      restaurant: { "Restaurant": 300, "Café": 250, "Snack": 200, "Kitchenette": 180 },
      parking: { "Parking extérieur": 40, "Parking souterrain": 80, "Abri vélos": 60, "Bornes de recharge": 200 },
      office: { "Bureau indépendant": 150, "Espace de coworking": 180, "Salle de réunion": 200, "Accueil": 250 },
      security: { "Vidéosurveillance": 100, "Système d'alarme": 80, "Contrôle d'accès": 120, "Porte blindée": 800 },
      access: { "Ascenseur": 1000, "Escalier": 200, "Accès sécurisé": 150, "Modifications d'accessibilité": 100 }
    };

    let totalEstimate = 0;
    const detailedPricing = [];

    categories.forEach(category => {
      const categoryPrices = basePrices[category.categoryKey] || {};
      category.subcategories.forEach(subcategory => {
        const pricePerM2 = categoryPrices[subcategory] || 50;
        const surface = (surfaces[subcategory]?.surface ?? surfaces[subcategory]) || category.estimatedSurface || 10;
        const subtotal = pricePerM2 * surface;

        totalEstimate += subtotal;
        detailedPricing.push({
          category: category.categoryTitle,
          subcategory,
          surface,
          pricePerM2,
          subtotal
        });
      });
    });

    const result = {
      totalEstimate: Math.round(totalEstimate),
      detailedPricing,
      priceRange: {
        min: Math.round(totalEstimate * 0.8),
        max: Math.round(totalEstimate * 1.3)
      }
    };

    return {
      ...result,
      formattedTotal: new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(result.totalEstimate)
    };
  };

  const convertManualSelectionToStructure = () => {
    // Regrouper les sous-catégories par catégorie
    const categoryMap = {};
    
    selectedSubcategories.forEach(subcategory => {
      // Trouver la catégorie parent de cette sous-catégorie
      const parentCategory = findParentCategory(subcategory);
      if (parentCategory) {
        if (!categoryMap[parentCategory.key]) {
          categoryMap[parentCategory.key] = {
            categoryKey: parentCategory.key,
            categoryTitle: parentCategory.title,
            subcategories: [],
            estimatedSurface: 0
          };
        }
        categoryMap[parentCategory.key].subcategories.push(subcategory);
        
        const surface = subcategorySurfaces[subcategory]?.surface || 10;
        categoryMap[parentCategory.key].estimatedSurface += parseFloat(surface);
      }
    });

    const categories = Object.values(categoryMap);
    const surfaces = { ...subcategorySurfaces };

    return {
      source: 'manual-selection',
      categories,
      surfaces,
      totalSurface: categories.reduce((sum, cat) => sum + cat.estimatedSurface, 0),
      complexity: categories.length > 3 ? 'high' : categories.length > 1 ? 'medium' : 'low'
    };
  };

  const findParentCategory = (subcategory) => {
    // Chercher dans les catégories services
    if (projectType === 'services') {
      const allCategories = {
        ...lang.fr.services.interior.categories,
        ...lang.fr.services.exterior.categories
      };
      
      for (const [key, category] of Object.entries(allCategories)) {
        if (category.subcategories?.includes(subcategory)) {
          return { key, title: category.title };
        }
      }
    }
    
    // Chercher dans les catégories professionnelles
    if (projectType === 'professional') {
      const professionalCategories = lang.fr.professional.categories;
      
      for (const [key, category] of Object.entries(professionalCategories)) {
        if (category.subcategories?.includes(subcategory)) {
          return { key, title: category.title };
        }
      }
    }
    
    return null;
  };

  if (!unifiedStructure) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Aperçu du devis</h3>
        <p className="text-gray-400">Veuillez utiliser la sélection manuelle (catégories/sous-catégories) pour obtenir une estimation.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-white">Devis Structuré</h3>
        <div className="text-sm text-gray-400">Source: Sélection manuelle</div>
      </div>

      {/* Résumé */}
      {unifiedStructure.summary && (
        <div className="bg-gray-700 rounded-lg p-4">
          <h4 className="text-white font-semibold mb-2">Résumé du projet</h4>
          <p className="text-gray-300">{unifiedStructure.summary}</p>
        </div>
      )}

      {/* Détail par catégories */}
      <div className="space-y-4">
        <h4 className="text-white font-semibold">Détail des travaux</h4>
        
        {unifiedStructure.categories.map((category, index) => (
          <div key={index} className="bg-gray-700 rounded-lg p-4">
            <div className="flex justify-between items-start mb-3">
              <h5 className="text-white font-medium">{category.categoryTitle}</h5>
              {category.confidence && (
                <span className="text-xs text-gray-400">
                  Confiance: {Math.round(category.confidence * 100)}%
                </span>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {category.subcategories.map((subcategory, subIndex) => {
                const pricingItem = pricing?.detailedPricing.find(p => 
                  p.category === category.categoryTitle && p.subcategory === subcategory
                );
                
                return (
                  <div key={subIndex} className="bg-gray-600 rounded p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">{subcategory}</span>
                      <div className="text-right">
                        {pricingItem && (
                          <>
                            {(() => {
                              // Supporter la nouvelle forme (quantity + unitLabel + pricePerUnit)
                              const quantity = pricingItem.quantity ?? pricingItem.surface;
                              const unitLabel = pricingItem.unitLabel ?? '€/m²';
                              const priceUnit = pricingItem.pricePerUnit ?? pricingItem.pricePerM2;
                              const unitText = unitLabel === '€/unité' ? 'unité(s)' : 'm²';

                              return (
                                <div className="text-sm text-gray-400">
                                  {quantity} {unitText} × {priceUnit}€/{unitText === 'unité(s)' ? 'unité' : 'm²'}
                                </div>
                              );
                            })()}
                            <div className="text-white font-semibold">
                              {pricingItem.subtotal.toLocaleString('fr-FR')} €
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Total */}
      {pricing && (
        <div className="bg-primary rounded-lg p-4">
          <div className="flex justify-between items-center">
            <div>
              <h4 className="text-white font-bold text-lg">Total estimé</h4>
              <p className="text-blue-100 text-sm">
                Surface totale: {unifiedStructure.totalSurface}m² • 
                Complexité: {unifiedStructure.complexity}
              </p>
            </div>
            <div className="text-white font-bold text-2xl">
              {pricing.formattedTotal}
            </div>
          </div>
        </div>
      )}

      {/* Informations complémentaires */}
      <div className="text-xs text-gray-400 border-t border-gray-600 pt-4">
        <p>• Les prix sont donnés à titre indicatif et peuvent varier selon les spécificités du projet</p>
        <p>• Un devis définitif sera établi après visite technique</p>
        
      </div>
    </div>
  );
};

export default DevisGenerator;